import React, {FC, ReactElement, useEffect, useRef, useState} from 'react';
import {Autocomplete, TextField} from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import MicIcon from '@mui/icons-material/Mic';
import {setInitialOutputDevicesAmount} from '../reducers/sessionReducer';
import {useAppDispatch, useAppSelector} from '../hooks/storeHooks';
import cn from 'classnames'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MediaDevicesUtils from "../utils/MediaDevicesUtils";
import MediaDeviceInfoWithState from "../utils/MediaDeviceInfoPlus";
import {getSessionContext} from "../contexts/SessionResolver";
import {useToast} from "./errors/ToastContext";
import {getCompactDeviceName, getFriendlyDeviceName} from "../utils/DeviceDisplayNames";
import {INPUT_ONLY_DEVICE_ID, OUTPUT_ONLY_DEVICE_ID} from "../contexts/Configuration";

const OUTPUT_ONLY_SENTINEL = {
    deviceId: OUTPUT_ONLY_DEVICE_ID,
    groupId: '',
    kind: 'audioinput' as MediaDeviceKind,
    label: 'No Input : Output Language Only',
    isActive: true,
    preselected: false,
    lastPanelId: '',
    toJSON: () => ({}),
} as unknown as MediaDeviceInfoWithState;

const INPUT_ONLY_SENTINEL = {
    deviceId: INPUT_ONLY_DEVICE_ID,
    groupId: '',
    kind: 'audiooutput' as MediaDeviceKind,
    label: 'No Output : Input Language Only',
    isActive: true,
    preselected: false,
    lastPanelId: '',
    toJSON: () => ({}),
} as unknown as MediaDeviceInfoWithState;

const TOAST_DEDUP_WINDOW_MS = 5000;
const recentDeviceToastByKey = new Map<string, number>();

const shouldEmitDeviceToast = (
    type: 'warning' | 'info' | 'error',
    message: string,
    dedupKey?: string
): boolean => {
    const key = dedupKey || `${type}:${message.trim().toLowerCase()}`;
    const now = Date.now();
    const previous = recentDeviceToastByKey.get(key);
    if (previous && (now - previous) < TOAST_DEDUP_WINDOW_MS) {
        return false;
    }
    recentDeviceToastByKey.set(key, now);
    return true;
};


/**
 * DeviceSelector - Audio device selection component with disconnection persistence
 *
 * ## Disconnected Device Behavior
 *
 * When a device is disconnected, it remains visible in the selector with visual indicators:
 *
 * 1. **During session**: If a selected device is disconnected:
 *    - Device stays in the selector (not removed)
 *    - Label shows "(inactive)" prefix via `getOptionLabel`
 *    - TextField gets "device-selected-disconnected" CSS class for styling
 *    - Toast notification warns the user (for input devices)
 *
 * 2. **On page reload/navigation**: Disconnected devices persist because:
 *    - MediaDevicesUtils stores device config in localStorage (`outputDevicesConfig`)
 *    - On enumerate, if cached device not found in API devices, it's added with `isActive = false`
 *    - Device is kept in cache for up to 10 days (`TEN_DAYS` constant)
 *    - After 10 days of being disconnected, device is automatically forgotten
 *
 * 3. **Visual styling** (defined in CSS):
 *    - `.device-selected-disconnected` class applied when `!device.isActive`
 *    - Label prefixed with "(inactive)" text
 *
 * 4. **Reconnection**: When device reconnects:
 *    - `isActive` flag is set back to `true`
 *    - Visual indicators are removed
 *    - Toast notification informs user (for input devices)
 *
 * @see MediaDevicesUtils.enumerateDevices() - handles disconnected device persistence
 * @see MediaDeviceInfoWithState.isActive - tracks connection state
 */
const DeviceSelector: FC<DeviceSelectorProps> = ({
                                                     onDeviceSelected,
                                                     kind,
                                                     onlyTextEnabled = false,
                                                     id,
                                                     excludedDeviceIds = [],
                                                     showOutputOnlyOption = false,
                                                     outputOnlySelected = false,
                                                     showInputOnlyOption = false,
                                                     inputOnlySelected = false,
                                                     labelPrefix,
                                                     shouldReconnectLiveInput = true,
                                                 }): ReactElement => {

    let sessionStarted = useAppSelector((state) => state.session.sessionStarted);
    // let input = useAppSelector((state) => state.session.input);
    let outputs = useAppSelector((state) => state.session.outputs);


    const dispatch = useAppDispatch();
    const {addToast} = useToast();

    const [currentDevice, setCurrentDevice] = useState<MediaDeviceInfoWithState | null>(null);
    const [availableDevices, setAvailableDevices] = useState<MediaDeviceInfoWithState[]>([]);
    const currentDeviceRef = useRef<MediaDeviceInfoWithState | null>();

    // Minimal F5 restore fallback for output panels:
    // if preselected flags are not ready yet, resolve remembered output from storage.
    const findRememberedOutputForPanel = (devices: MediaDeviceInfoWithState[]): MediaDeviceInfoWithState | undefined => {
        if (kind !== 'audiooutput') {
            return undefined;
        }

        const cache = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as any;
        const remembered = cache?.outputDevicesConfig?.[id] as Partial<MediaDeviceInfoWithState> | undefined;
        if (!remembered) {
            return undefined;
        }

        const byId = remembered.deviceId
            ? devices.find(d => d.deviceId === remembered.deviceId)
            : undefined;
        if (byId) {
            return byId;
        }

        if (remembered.groupId) {
            const byGroup = devices.filter(d => d.groupId && d.groupId === remembered.groupId);
            if (byGroup.length === 1) {
                return byGroup[0];
            }
            if (byGroup.length > 1 && remembered.label) {
                const normalized = remembered.label.trim().toLowerCase();
                const narrowed = byGroup.filter(d => d.label?.trim().toLowerCase() === normalized);
                if (narrowed.length === 1) {
                    return narrowed[0];
                }
            }
        }

        if (remembered.label) {
            const normalized = remembered.label.trim().toLowerCase();
            const byLabel = devices.filter(d => d.label?.trim().toLowerCase() === normalized);
            if (byLabel.length === 1) {
                return byLabel[0];
            }
        }

        return undefined;
    };

    // Some USB/Bluetooth devices can come back with a different deviceId after replug.
    // Match by stable metadata so we can restore selection and reconnect automatically.
    const findReplacementForCurrentDevice = (
        current: MediaDeviceInfoWithState,
        devices: MediaDeviceInfoWithState[]
    ): MediaDeviceInfoWithState | undefined => {
        if (!current?.deviceId) return undefined;

        const activeCandidates = devices.filter(d => d.kind === current.kind && d.isActive);
        if (activeCandidates.length === 0) return undefined;

        if (current.groupId) {
            const byGroup = activeCandidates.filter(d => d.groupId && d.groupId === current.groupId);
            if (byGroup.length === 1) return byGroup[0];
        }

        const normalize = (label?: string) => (label || '').trim().toLowerCase();
        const byLabel = activeCandidates.filter(d => normalize(d.label) === normalize(current.label));
        if (byLabel.length === 1) return byLabel[0];

        return undefined;
    };

    const reconnectLiveInputIfRunning = (device: MediaDeviceInfoWithState) => {
        if (!shouldReconnectLiveInput) return;

        const session = getSessionContext();
        if (!session) return;

        const resolveReconnectCandidate = (): MediaDeviceInfoWithState | null => {
            if (device?.isActive) return device;
            const inputs = MediaDevicesUtils.instance().getDevices('audioinput');
            const replacement = findReplacementForCurrentDevice(device, inputs);
            if (replacement?.isActive) return replacement;
            return null;
        };

        const tryReconnect = (): boolean => {
            const candidate = resolveReconnectCandidate();
            if (!candidate) return false;
            try {
                session.changeInputDevice(candidate);
                return true;
            } catch (e) {
                console.error('[DeviceSelector] Failed to reconnect input device:', e);
                return false;
            }
        };

        if (tryReconnect()) return;

        // Device re-enumeration and track readiness may lag briefly after reconnect.
        // Retry once before surfacing a warning toast to avoid false positives.
        window.setTimeout(() => {
            if (tryReconnect()) return;
            // If selector state already shows the device as active, skip warning:
            // session is effectively recovered and this was a transient race.
            const latestInputs = MediaDevicesUtils.instance().getDevices('audioinput');
            const latest = latestInputs.find(d => d.deviceId === (currentDeviceRef.current?.deviceId || device.deviceId));
            if (latest?.isActive) return;
            const message = 'Input device reconnected but could not be attached to the active session automatically.';
            if (shouldEmitDeviceToast('warning', message, `input-reconnect-failed:${currentDeviceRef.current?.deviceId || device.deviceId}`)) {
                addToast({message, type: 'warning'});
            }
        }, 500);
    };

    const syncLiveOutputIfRunning = (device: MediaDeviceInfoWithState, becameActive: boolean) => {
        if (kind !== 'audiooutput') return;
        if (!sessionStarted) return;
        if (!device?.deviceId) return;

        const session = getSessionContext();
        if (!session) return;

        const outputConfig = outputs.find(o => o.id === id);
        const translationKey = outputConfig?.language?.translationKey;
        if (!translationKey) return;

        const outputGain = typeof outputConfig.gain === 'number' ? outputConfig.gain : 1.0;
        const targetGain = device.isActive ? outputGain : 0;
        try {
            // Match two-way reconnect behavior:
            // - While disconnected, keep selected device state but mute output to prevent
            //   audible fallback routing to another system endpoint.
            // - On reconnect, restore gain and force sink rebind if needed.
            if (!device.isActive) {
                session.getText2Speech(translationKey)?.getAudioPlayer()?.setVolume(0);
                return;
            }
            session.setOutputDevice(translationKey, device, targetGain, becameActive);
            session.getText2Speech(translationKey)?.getAudioPlayer()?.setVolume(targetGain);
        } catch (e) {
            console.error('[DeviceSelector] Failed to reconnect output device:', e);
            const message = device.isActive
                ? 'Output device reconnected but could not be attached to the active session automatically.'
                : 'Output device was disconnected and could not be detached cleanly from the active session.';
            if (shouldEmitDeviceToast('warning', message, `${kind}-sync-failed:${device.deviceId}:${device.isActive ? 'active' : 'inactive'}`)) {
                addToast({message, type: 'warning'});
            }
        }
    };

    const changeSelectedDevice = (device: MediaDeviceInfoWithState | null) => {
        setCurrentDevice(device);
        onDeviceSelected(device);
        currentDeviceRef.current = device;

        // Sentinels are not real devices — skip persistence
        if (device?.deviceId === OUTPUT_ONLY_DEVICE_ID || device?.deviceId === INPUT_ONLY_DEVICE_ID) return;

        if (device?.deviceId) {
            // here I need to make a copy of the device so I can mutate it
            let _device: MediaDeviceInfoWithState = MediaDevicesUtils.instance().copyDevice(device);

            if (kind === 'audioinput')
                MediaDevicesUtils.instance().rememberSelectedDevice(_device as MediaDeviceInfoWithState, true)
            else
                MediaDevicesUtils.instance().rememberSelectedOutputDevice(_device as MediaDeviceInfoWithState, id, true)
        } else {
            if (kind === 'audioinput')
                MediaDevicesUtils.instance().forgetSelectedDevice();
            else
                MediaDevicesUtils.instance().forgetSelectedOutputDevice(id);
        }

        // console.log("posting event selected !!!!!!!! ", device)
        if (device?.kind === 'audioinput') {
            window.postMessage({
                type: 'mediadevice-selected',
                deviceId: device.deviceId
            }, '*');
        }
    }

    useEffect(() => {
            let _devices: MediaDeviceInfoWithState[] = MediaDevicesUtils.instance().getDevices(kind);
            setAvailableDevices(_devices);
            // console.log("Device selector init: ", id, kind, _devices);

            if (kind === 'audiooutput')
                dispatch(setInitialOutputDevicesAmount(_devices.length));

            // Pre-select the output-only sentinel if this panel was saved as output-only
            if (showOutputOnlyOption && outputOnlySelected) {
                setCurrentDevice(OUTPUT_ONLY_SENTINEL);
                currentDeviceRef.current = OUTPUT_ONLY_SENTINEL;
                onDeviceSelected(OUTPUT_ONLY_SENTINEL);
                const onMessage = (e: MessageEvent) => {
                    if (e.data && e.data.type === 'mediadevices-changed') {
                        const _updatedDevices = MediaDevicesUtils.instance().getDevices(kind);
                        setAvailableDevices(_updatedDevices);
                        if (kind === 'audiooutput') dispatch(setInitialOutputDevicesAmount(_updatedDevices.length));
                    }
                };
                window.addEventListener('message', onMessage);
                return () => window.removeEventListener('message', onMessage);
            }

            // Pre-select the input-only sentinel if this panel was saved as input-only
            if (showInputOnlyOption && inputOnlySelected) {
                setCurrentDevice(INPUT_ONLY_SENTINEL);
                currentDeviceRef.current = INPUT_ONLY_SENTINEL;
                onDeviceSelected(INPUT_ONLY_SENTINEL);
                const onMessage = (e: MessageEvent) => {
                    if (e.data && e.data.type === 'mediadevices-changed') {
                        const _updatedDevices = MediaDevicesUtils.instance().getDevices(kind);
                        setAvailableDevices(_updatedDevices);
                        if (kind === 'audiooutput') dispatch(setInitialOutputDevicesAmount(_updatedDevices.length));
                    }
                };
                window.addEventListener('message', onMessage);
                return () => window.removeEventListener('message', onMessage);
            }

            let haveDeviceToPreSelect;
            if (kind === 'audioinput') {
                haveDeviceToPreSelect = _devices.find(d => d.preselected)
                // console.log("Remembered Input Device: ", haveDeviceToPreSelect)

            }

            if (kind === 'audiooutput') {
                haveDeviceToPreSelect = _devices.find(d => d.preselected && d.lastPanelId === id);
                if (!haveDeviceToPreSelect) {
                    haveDeviceToPreSelect = findRememberedOutputForPanel(_devices);
                }
                // console.log("Remembered Output Device: ", haveDeviceToPreSelect)
            }

            if (!!haveDeviceToPreSelect) {
                //console.log("Preselecting device: ", kind, haveDeviceToPreSelect)
                changeSelectedDevice(haveDeviceToPreSelect);
            }

            // Listen for changes: added or removed devices
            const onMessage = (e: MessageEvent) => {
                if (e.data && e.data.type === 'mediadevices-changed') {

                    let _changes: {
                        newDevices: string[]
                        reconnectedDevices: string[]
                        disconnectedDevices: string[]
                    } = e.data.changes;

                    const hasChanges = !!e.data.changes &&
                        ((_changes.disconnectedDevices.length > 0) || (_changes.newDevices.length > 0) || (_changes.reconnectedDevices.length > 0));

                    const _currentDevice = currentDeviceRef.current;
                    console.debug(`DeviceSelector - currentDevice: ${_currentDevice?.deviceId}`)

                    const _updatedDevices = MediaDevicesUtils.instance().getDevices(kind);
                    setAvailableDevices(_updatedDevices)

                    // Preserve old reconnect logic, but if nothing is selected yet after refresh,
                    // restore remembered selection when device list arrives.
                    if (!_currentDevice) {
                        const remembered = kind === 'audioinput'
                            ? _updatedDevices.find(d => d.preselected)
                            : (_updatedDevices.find(d => d.preselected && d.lastPanelId === id)
                                || findRememberedOutputForPanel(_updatedDevices));

                        if (remembered) {
                            changeSelectedDevice(remembered);
                        }
                    }

                    // No connect/disconnect transitions to process; list refresh/restore already handled above.
                    if (!hasChanges) {
                        if (kind === 'audiooutput') {
                            dispatch(setInitialOutputDevicesAmount(_updatedDevices.length));
                        }
                        return;
                    }

                    if (!!_currentDevice) {
                        console.debug(`DeviceSelector - kind: ${kind} - currentDevice: ${_currentDevice.deviceId} - changes: ${JSON.stringify(_changes)}`)
                        let _updatedDevice = _updatedDevices?.find(d => d.deviceId === _currentDevice?.deviceId);
                        if (!_updatedDevice) {
                            if (_currentDevice.kind === 'audioinput') {
                                const replacement = findReplacementForCurrentDevice(_currentDevice, _updatedDevices);
                                if (replacement) {
                                    let message = 'Reconnecting input device: ' + replacement.label;
                                    if (shouldEmitDeviceToast('info', message, `input-reconnected:${replacement.deviceId}`)) {
                                        addToast({message: message, type: 'info'});
                                    }
                                    reconnectLiveInputIfRunning(replacement);
                                    changeSelectedDevice(replacement);
                                    if (kind === 'audiooutput') {
                                        dispatch(setInitialOutputDevicesAmount(_updatedDevices.length));
                                    }
                                    return;
                                }
                            }
                            if (_currentDevice.kind === 'audiooutput') {
                                const replacement = findReplacementForCurrentDevice(_currentDevice, _updatedDevices);
                                const replacementJustConnected = !!replacement && (
                                    _changes.reconnectedDevices?.includes(replacement.deviceId) ||
                                    _changes.newDevices?.includes(replacement.deviceId)
                                );
                                if (replacement && replacementJustConnected) {
                                    const message = 'Reconnecting output device: ' + replacement.label;
                                    if (shouldEmitDeviceToast('info', message, `output-reconnected:${replacement.deviceId}`)) {
                                        addToast({message, type: 'info'});
                                    }
                                    syncLiveOutputIfRunning(replacement, true);
                                    changeSelectedDevice(replacement);
                                    if (kind === 'audiooutput') {
                                        dispatch(setInitialOutputDevicesAmount(_updatedDevices.length));
                                    }
                                    return;
                                }
                            }
                            (_currentDevice as MediaDeviceInfoWithState).isActive = false;
                            if (_currentDevice.kind === 'audiooutput') {
                                syncLiveOutputIfRunning(_currentDevice as MediaDeviceInfoWithState, false);
                            }
                            changeSelectedDevice(_currentDevice)
                        } else {
                            const wasDisconnected = _changes.disconnectedDevices?.find(id => id === _currentDevice?.deviceId);
                            if (wasDisconnected) {
                                const replacement = findReplacementForCurrentDevice(_currentDevice, _updatedDevices);
                                const replacementJustConnected = !!replacement && (
                                    _changes.reconnectedDevices?.includes(replacement.deviceId) ||
                                    _changes.newDevices?.includes(replacement.deviceId)
                                );
                                if (replacement && replacementJustConnected) {
                                    if (_currentDevice.kind === 'audioinput') {
                                        const message = 'Reconnecting input device: ' + replacement.label;
                                        if (shouldEmitDeviceToast('info', message, `input-reconnected:${replacement.deviceId}`)) {
                                            addToast({message, type: 'info'});
                                        }
                                        reconnectLiveInputIfRunning(replacement);
                                    } else if (_currentDevice.kind === 'audiooutput') {
                                        const message = 'Reconnecting output device: ' + replacement.label;
                                        if (shouldEmitDeviceToast('info', message, `output-reconnected:${replacement.deviceId}`)) {
                                            addToast({message, type: 'info'});
                                        }
                                        syncLiveOutputIfRunning(replacement, true);
                                    }
                                    changeSelectedDevice(replacement);
                                    if (kind === 'audiooutput') {
                                        dispatch(setInitialOutputDevicesAmount(_updatedDevices.length));
                                    }
                                    return;
                                }

                                if (_currentDevice.kind === 'audioinput') {
                                    let message = 'Input device was disconnected: ' + _currentDevice.label;
                                    if (shouldEmitDeviceToast('warning', message, `input-disconnected:${_currentDevice.deviceId}`)) {
                                        addToast({message: message, type: 'warning'});
                                    }
                                } else if (_currentDevice.kind === 'audiooutput') {
                                    const message = 'Output device was disconnected: ' + _currentDevice.label;
                                    if (shouldEmitDeviceToast('warning', message, `output-disconnected:${_currentDevice.deviceId}`)) {
                                        addToast({message, type: 'warning'});
                                    }
                                }
                                if (_updatedDevice.kind === 'audiooutput') {
                                    syncLiveOutputIfRunning(_updatedDevice, false);
                                }
                                changeSelectedDevice(_updatedDevice)
                            } else {
                                const wasReconnected = _changes.reconnectedDevices?.find(id => id === _currentDevice?.deviceId);
                                if (wasReconnected) {
                                    if (_currentDevice.kind === 'audioinput') {
                                        let message = 'Reconnecting input device: ' + _currentDevice.label;
                                        if (shouldEmitDeviceToast('info', message, `input-reconnected:${_currentDevice.deviceId}`)) {
                                            addToast({message: message, type: 'info'});
                                        }
                                    } else if (_currentDevice.kind === 'audiooutput') {
                                        const message = 'Reconnecting output device: ' + _currentDevice.label;
                                        if (shouldEmitDeviceToast('info', message, `output-reconnected:${_currentDevice.deviceId}`)) {
                                            addToast({message, type: 'info'});
                                        }
                                    }

                                    if (_updatedDevice.kind === 'audioinput') {
                                        reconnectLiveInputIfRunning(_updatedDevice);
                                    }
                                    if (_updatedDevice.kind === 'audiooutput') {
                                        syncLiveOutputIfRunning(_updatedDevice, true);
                                    }
                                    changeSelectedDevice(_updatedDevice)
                                }
                            }
                        }
                    }

                    if (kind === 'audiooutput')
                        dispatch(setInitialOutputDevicesAmount(_updatedDevices.length));
                }
            };
            window.addEventListener('message', onMessage);
            return () => window.removeEventListener('message', onMessage);
        }, []
    );

    /*
    navigator.mediaDevices.addEventListener('devicechange', event => {
            getDevices(kind).then(devices => {
                setAvailableDevices(devices);
                dispatch(setInitialOutputDevicesAmount(devices.length));
            }).catch((err) => {
                console.error(`DeviceSelector - kind: ${kind} - ${err.name}: ${err.message}`);
            });
        });
     */

    useEffect(() => {
        if (kind === 'audiooutput' && onlyTextEnabled) {
            setCurrentDevice(null);
            onDeviceSelected(null);
        }
    }, [onlyTextEnabled]);

    // One-time-like fallback on device list readiness (covers F5 init order races).
    useEffect(() => {
        if (currentDevice || availableDevices.length === 0) return;

        const remembered = kind === 'audioinput'
            ? availableDevices.find(d => d.preselected)
            : (availableDevices.find(d => d.preselected && d.lastPanelId === id)
                || findRememberedOutputForPanel(availableDevices));

        if (remembered) {
            changeSelectedDevice(remembered);
        }
    }, [availableDevices]); // eslint-disable-line react-hooks/exhaustive-deps

    // When a selected output device is taken by another panel, clear this selection
    useEffect(() => {
        if (kind === 'audiooutput' && currentDevice?.deviceId && excludedDeviceIds.includes(currentDevice.deviceId)) {
            changeSelectedDevice(null);
        }
    }, [excludedDeviceIds]); // eslint-disable-line react-hooks/exhaustive-deps

    const usedDeviceIds = [...outputs.filter(o => o.device).map(o => o.device.deviceId), ...excludedDeviceIds];
    const startAdornmentIcon = onlyTextEnabled
        ? <VolumeOffIcon fontSize="small"/>
        : kind === 'audioinput'
            ? <MicIcon fontSize="small"/>
            : <VolumeUpIcon fontSize="small"/>;

    const startAdornment = labelPrefix != null
        ? (
            <>
                {startAdornmentIcon}
                <span className="device-selector-label-prefix">
                    {labelPrefix}
                </span>
            </>
        )
        : startAdornmentIcon;
    const selectorDomId = `selectableDevices_${kind}_${id}`;
    const selectorInputName = `${kind}_${id}_device`;

    const selectorOptions: MediaDeviceInfoWithState[] = (() => {
        const realDevices = availableDevices
            .filter(d => {
                // Keep current selection visible so MUI Autocomplete value always
                // matches at least one option and does not emit invalid-value warnings.
                if (currentDevice?.deviceId && d.deviceId === currentDevice.deviceId) return true;
                // For output selectors, show inactive devices only when they belong
                // to this panel. This avoids unrelated stale inactive entries leaking
                // into other panel dropdowns.
                if (kind === 'audiooutput' && !(d as MediaDeviceInfoWithState).isActive) {
                    return (d as MediaDeviceInfoWithState).lastPanelId === id;
                }
                if (usedDeviceIds.includes(d.deviceId)) return false;
                // Remove the Communications endpoint from output dropdowns
                if (kind === 'audiooutput' && d.deviceId === 'communications') return false;
                return true;
            })
            .sort((a, b) => {
                const isAComm = a.deviceId === 'communications';
                const isBComm = b.deviceId === 'communications';
                if (isAComm && !isBComm) return 1;
                if (!isAComm && isBComm) return -1;
                return 0;
            });
        // OUTPUT_ONLY: second-to-last in input dropdown
        let withOutputOnly: MediaDeviceInfoWithState[] = realDevices;
        if (showOutputOnlyOption) {
            withOutputOnly = realDevices.length === 0
                ? [OUTPUT_ONLY_SENTINEL]
                : [...realDevices.slice(0, -1), OUTPUT_ONLY_SENTINEL, ...realDevices.slice(-1)];
        }
        // INPUT_ONLY: last in output dropdown
        return showInputOnlyOption ? [...withOutputOnly, INPUT_ONLY_SENTINEL] : withOutputOnly;
    })();

    return (
        <Autocomplete<MediaDeviceInfoWithState, false, false, false>
            id={selectorDomId}
            disabled={sessionStarted || onlyTextEnabled}
            disableClearable={false}
            clearOnEscape
            className={cn('device-selector', {'disable': onlyTextEnabled})}
            // options={selectorOptions}
            options={(() => {
                const realDevices = availableDevices
                    .filter(d => {
                        // Keep current selection visible so MUI Autocomplete value always
                        // matches at least one option and does not emit invalid-value warnings.
                        if (currentDevice?.deviceId && d.deviceId === currentDevice.deviceId) return true;
                        // For output selectors, show inactive devices only when they belong
                        // to this panel. This avoids unrelated stale inactive entries leaking
                        // into other panel dropdowns.
                        if (kind === 'audiooutput' && !(d as MediaDeviceInfoWithState).isActive) {
                            return (d as MediaDeviceInfoWithState).lastPanelId === id;
                        }
                        if (usedDeviceIds.includes(d.deviceId)) return false;
                        // Remove the Communications endpoint from output dropdowns
                        if (kind === 'audiooutput' && d.deviceId === 'communications') return false;
                        return true;
                    })
                    .sort((a, b) => {
                        const isAComm = a.deviceId === 'communications';
                        const isBComm = b.deviceId === 'communications';
                        if (isAComm && !isBComm) return 1;
                        if (!isAComm && isBComm) return -1;
                        return 0;
                    });
                // OUTPUT_ONLY: second-to-last in input dropdown
                let withOutputOnly = realDevices;
                if (showOutputOnlyOption) {
                    withOutputOnly = realDevices.length === 0
                        ? [OUTPUT_ONLY_SENTINEL]
                        : [...realDevices.slice(0, -1), OUTPUT_ONLY_SENTINEL, ...realDevices.slice(-1)];
                }
                // INPUT_ONLY: last in output dropdown
                return showInputOnlyOption ? [...withOutputOnly, INPUT_ONLY_SENTINEL] : withOutputOnly;
            })()}
            ListboxProps={{style: {maxHeight: 160}, className: 'device-selector-listbox'}}
            popupIcon={<ExpandMoreIcon/>}
            getOptionLabel={op => {
                if (op.deviceId === OUTPUT_ONLY_DEVICE_ID || op.deviceId === INPUT_ONLY_DEVICE_ID) return op.label;
                const compact = getCompactDeviceName(op.label);
                if (!(op as MediaDeviceInfoWithState).isActive)
                    return `(inactive) ${compact}`;
                return compact;
            }}
            renderOption={(props, op) => {
                const {key, ...optionProps} = props as React.HTMLAttributes<HTMLElement> & { key: React.Key };
                if (op.deviceId === OUTPUT_ONLY_DEVICE_ID || op.deviceId === INPUT_ONLY_DEVICE_ID)
                    return <li key={key} {...optionProps}><em>{op.label}</em></li>;
                const full = getFriendlyDeviceName(op.label);
                const label = !(op as MediaDeviceInfoWithState).isActive
                    ? `(inactive) ${full}`
                    : full;
                return <li key={key} {...optionProps}>{label}</li>;
            }}
            isOptionEqualToValue={(option, value) => {
                return option.deviceId === value.deviceId;
            }}
            value={currentDevice}
            onChange={(_e, selectedDevice) => {
                changeSelectedDevice(selectedDevice)
            }}

            renderInput={(params) => {
                const placeholder =
                    kind === 'audioinput'
                        ? 'Select Input Device'
                        : 'Select Output Device';
                const isEmpty = !currentDevice && !sessionStarted && !onlyTextEnabled;

                return (
                    <TextField
                        {...params}
                        size="small"
                        className={cn("device-selector-label", {
                            "device-selected-disconnected": currentDevice && !(currentDevice as MediaDeviceInfoWithState).isActive,
                            "device-selector-label--required": isEmpty,
                        })}
                        placeholder={placeholder}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment,
                        }}
                        inputProps={{
                            ...params.inputProps,
                            name: selectorInputName,
                        }}
                    />
                );
            }}
        />
    );
}


type DeviceSelectorProps = {
    onDeviceSelected: (device: MediaDeviceInfo | null) => void,
    kind: MediaDeviceKind,
    onlyTextEnabled?: boolean,
    id: string,
    preferredDeviceId?: string | null,
    excludedDeviceIds?: string[],
    showOutputOnlyOption?: boolean,
    outputOnlySelected?: boolean,
    showInputOnlyOption?: boolean,
    inputOnlySelected?: boolean,
    /** Optional label before the icon (e.g. "In:  " or "Out: ") for alignment in two-way Dashboard */
    labelPrefix?: string,
    /** For two-way mode: only active input panel should trigger live session microphone reattach. */
    shouldReconnectLiveInput?: boolean,
}

export default DeviceSelector;
